import { type CSSProperties, FC, LazyExoticComponent, Suspense } from 'react';
import type { SvgProps } from 'react-native-svg';
import { theme } from 'twin.macro';

export type { SvgProps as IconProps } from 'react-native-svg';

export type Size = 'extra-small' | 'small' | 'medium' | 'large';

export type IconSourceComponent = FC<SvgProps>;
export type IconSource = IconSourceComponent | LazyExoticComponent<IconSourceComponent>;

export interface Props extends Omit<SvgProps, 'color' | 'focusable' | 'style'> {
	/**
	 * Changes the size of the icon
	 * @default 'medium'
	 */
	size?: Size;
	source: IconSource;
	color?: string;
	focusable?: boolean;
	style?: CSSProperties;
}

const getSize = (size?: Size) => {
	switch (size) {
		case 'extra-small': {
			return {
				height: 12,
				width: 12,
			};
		}
		case 'small': {
			return {
				height: 16,
				width: 16,
			};
		}
		default:
		case 'medium': {
			return {
				height: 24,
				width: 24,
			};
		}
		case 'large': {
			return {
				height: 32,
				width: 32,
			};
		}
	}
};

export const Icon: FC<Props> = ({
	color = theme`colors.gray.700`,
	focusable = false,
	size = 'medium',
	source: SourceComponent,
	style,
	...rest
}) => (
	<Suspense fallback={<span aria-hidden style={{ ...getSize(size), flexShrink: 0 }} />}>
		<SourceComponent
			color={color}
			focusable={focusable}
			{...rest}
			{...getSize(size)}
			style={{ flexShrink: 0, ...(style ?? {}) }}
		/>
	</Suspense>
);
